import { TestParserOptions } from '../../options/testParserOptions.js';
import { Test, TestType } from '../../parser/models/all.js';
import { PostProcessContext } from '../models/postProcessContext.js';
import { createTestCaseFn } from './postProcessUtils.js';

/**
 * Post-processor that expands pure functions with test cases into individual test entries.
 * 
 * @param test - The test representing a function with parameterized cases.
 * @param index - The index of the test in the collection.
 * @param context - The post-processing context.
 * @returns True if the test was processed and expanded.
 */
export function processTestCases(test: Test, index: number, context: PostProcessContext): boolean {
  if (test.type.has(TestType.FUNCTION_WITH_CASES)) {
    const testCaseProperties = createTestCases(test, context.options);
    context.postTests.push(...testCaseProperties);
    return true;
  }

  return false;
}

/**
 * Creates individual Test instances for each parameterized case of a pure function.
 * 
 * @param test - The template test containing the function and cases.
 * @param options - Parser options for context lookup and title templating.
 * @returns An array of expanded Test instances.
 */
function createTestCases(test: Test, options: TestParserOptions): Test[] {
  const {
    name,
    value: testFn,
    groupName,
    testCases
  } = test;

  const testContextLookup = options.testContextLookup;

  const results: Test[] = [];
  testCases.forEach(
    (testCasesArgs, testCaseIndex) => {
      const testCaseArgs = [...testCasesArgs, testCaseIndex];

      const testCaseFn = createTestCaseFn(
        groupName,
        testContextLookup,
        testFn,
        testCaseArgs
      );

      const newTestCase = new Test(
        test.groupName,
        options.testCaseTitleTemplate(name, testCasesArgs, testCaseIndex),
        testCaseFn,
        test.type,
        test.testCases,
        test.onlyExpression
      );

      results.push(newTestCase);
    }
  )

  return results;
}